import type { ReactNode } from 'react';
import React, {
	Fragment,
	useEffect,
	useState,
	useContext,
	useCallback,
	useRef,
	useMemo,
} from 'react';
import { styled } from '@compiled/react';
import {
	ForesightManager,
	type ForesightRegisterOptionsWithoutElement,
	type ForesightRegisterResult,
} from 'js.foresight';

import { EditorPreloadManager } from '@confluence/editor-preloader/entry-points/EditorPreloadManager.ts';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import { EditContentButtonPreloaderContext } from './EditContentButtonPreloaderContext.ts';

// Foresight relies on the browser exposing the JavaScript API for evaluating CSS media queries at runtime. Check for it so
// predictive prefetch only runs in browser environments that expose the API.
// Evaluated lazily (as a function) so that test environments can mock window.matchMedia before it is checked.
const getHasMatchMedia = () =>
	typeof window !== 'undefined' && typeof window.matchMedia === 'function';

// Copied from https://foresightjs.com/docs/react/hook
function useForesight<T extends HTMLElement = HTMLElement>(
	options: ForesightRegisterOptionsWithoutElement,
	/** @default false */
	enabled?: boolean,
) {
	const elementRef = useRef<T>(null);
	const registerResults = useRef<ForesightRegisterResult | null>(null);
	useEffect(() => {
		if (!elementRef.current || !enabled) {
			return;
		}

		// Initialize ForesightManager only when the runtime supports it.
		if (!getHasMatchMedia()) {
			return;
		}

		ForesightManager.initialize({
			// Mouse trajectory settings
			enableMousePrediction: true,
			trajectoryPredictionTime: expVal(
				'platform_editor_predictive_collabdraft_prefetch',
				'trajectoryPredictionTime',
				120,
			),
			positionHistorySize: 8,
		});

		const currentElement = elementRef.current;
		registerResults.current = ForesightManager.instance.register({
			element: currentElement,
			...options,
		});

		return () => {
			ForesightManager.instance?.unregister(currentElement, 'useEffect');
		};
	}, [options, enabled]);

	return { elementRef, registerResults };
}

type PrefetchAnalyticsAction = 'prefetchAndClick' | 'prefetchUnused';

export type SendPrefetchAnalyticsEvent = (
	action: PrefetchAnalyticsAction,
	contentId: string,
	attributes: Record<string, unknown>,
) => void;

type PendingPrefetchAnalyticsEvent = {
	firedAt: number;
	timeoutId: ReturnType<typeof setTimeout>;
};

const PREFETCH_CLICK_WINDOW_MS = 15 * 1000;
const pendingPrefetchAnalyticsEvents = new Map<string, PendingPrefetchAnalyticsEvent>();

const clearPendingPrefetchAnalytics = (contentId: string) => {
	const pendingPrefetch = pendingPrefetchAnalyticsEvents.get(contentId);
	if (pendingPrefetch) {
		clearTimeout(pendingPrefetch.timeoutId);
		pendingPrefetchAnalyticsEvents.delete(contentId);
	}
};

const registerPendingPrefetchAnalytics = ({
	contentId,
	sendPrefetchAnalyticsEvent,
}: {
	contentId: string;
	sendPrefetchAnalyticsEvent: SendPrefetchAnalyticsEvent;
}) => {
	// If there's an existing pending prefetch event for this content that hasn't
	// resolved or timed out yet, fire a prefetchUnused event for it before
	// replacing it with the new one.
	const pendingPrefetch = pendingPrefetchAnalyticsEvents.get(contentId);
	clearPendingPrefetchAnalytics(contentId);
	if (pendingPrefetch) {
		sendPrefetchAnalyticsEvent('prefetchUnused', contentId, {
			clickWindowMs: PREFETCH_CLICK_WINDOW_MS,
			prefetchSource: 'foresightjs',
		});
	}

	const firedAt = Date.now();
	const timeoutId = setTimeout(() => {
		const pendingPrefetch = pendingPrefetchAnalyticsEvents.get(contentId);
		if (!pendingPrefetch || pendingPrefetch.firedAt !== firedAt) {
			return;
		}

		pendingPrefetchAnalyticsEvents.delete(contentId);
		sendPrefetchAnalyticsEvent('prefetchUnused', contentId, {
			clickWindowMs: PREFETCH_CLICK_WINDOW_MS,
			prefetchSource: 'foresightjs',
		});
	}, PREFETCH_CLICK_WINDOW_MS);

	pendingPrefetchAnalyticsEvents.set(contentId, { firedAt, timeoutId });
};

export const resolvePendingPrefetchAnalytics = (
	contentId: string,
	sendPrefetchAnalyticsEvent: SendPrefetchAnalyticsEvent,
): void => {
	const pendingPrefetch = pendingPrefetchAnalyticsEvents.get(contentId);
	if (!pendingPrefetch) {
		return;
	}

	const timeToClickMs = Date.now() - pendingPrefetch.firedAt;

	clearTimeout(pendingPrefetch.timeoutId);
	pendingPrefetchAnalyticsEvents.delete(contentId);
	sendPrefetchAnalyticsEvent('prefetchAndClick', contentId, {
		clickWindowMs: PREFETCH_CLICK_WINDOW_MS,
		prefetchSource: 'foresightjs',
		timeToClickMs,
	});
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Styling div for hover area only
const HoverAreaWrapper = styled.div({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Pseudo-element to expand hover area
	'&::before': {
		content: '""',
		position: 'absolute',
		inset: '-8px',
	},
});

interface BaseEditContentButtonPreloaderProps {
	contentId: string;
	spaceKey?: string;
	isEmbeddedEditor?: boolean;
	children: ReactNode;
	editSource: string;
	predictivelyPrefetch?: boolean;
	sendPrefetchAnalyticsEvent?: SendPrefetchAnalyticsEvent;
}

interface PredictiveEditContentButtonPreloaderProps extends BaseEditContentButtonPreloaderProps {
	predictivelyPrefetch: true;
	sendPrefetchAnalyticsEvent: SendPrefetchAnalyticsEvent;
}

interface NonPredictiveEditContentButtonPreloaderProps extends BaseEditContentButtonPreloaderProps {
	predictivelyPrefetch?: false;
	sendPrefetchAnalyticsEvent?: never;
}

export type EditContentButtonPreloaderProps =
	| PredictiveEditContentButtonPreloaderProps
	| NonPredictiveEditContentButtonPreloaderProps;

export const EditContentButtonPreloader = ({
	contentId,
	spaceKey,
	children,
	editSource,
	isEmbeddedEditor,
	sendPrefetchAnalyticsEvent,
	predictivelyPrefetch,
}: EditContentButtonPreloaderProps): React.JSX.Element => {
	const preloader = useContext(EditContentButtonPreloaderContext); // See packages/confluence-fabric-editor/src/editorPreloader.tsx

	const [doPreload, setDoPreload] = useState(false);
	const doPreloadRef = useRef(doPreload);

	const [{ editSource: contextEditSource }, { setEditSource }] = useEditSource();

	useEffect(() => {
		// Keep the ref in sync with the latest doPreload value so that the function returned by
		// cleanup editorPreloadManager always uses the current doPreload value instead of the one before.
		doPreloadRef.current = doPreload;
	}, [doPreload]);

	// Cleanup editorPreloadManager on contentId change and unmount.
	useEffect(() => {
		return () => {
			clearPendingPrefetchAnalytics(contentId);
			if (doPreloadRef.current) {
				EditorPreloadManager.removePreloadedId(contentId);
			}
		};
	}, [contentId]);

	// Register edit-button element with ForesightJS for mouse trajectory prediction
	// Only call useForesight when predictive prefetch is enabled
	const foresightOptions = useMemo(
		() =>
			({
				callback: async () => {
					if (EditorPreloadManager.shouldPreload(contentId) && predictivelyPrefetch) {
						setDoPreload(true);
						setEditSource(editSource);
						registerPendingPrefetchAnalytics({
							contentId,
							sendPrefetchAnalyticsEvent,
						});
					}
				},
				name: `edit-button-${contentId}`,
				reactivateAfter: 15_000,
			}) satisfies ForesightRegisterOptionsWithoutElement,
		[
			contentId,
			editSource,
			setEditSource,
			setDoPreload,
			predictivelyPrefetch,
			sendPrefetchAnalyticsEvent,
		],
	);
	const { elementRef } = useForesight<HTMLDivElement>(foresightOptions, predictivelyPrefetch);

	// On hover callback
	const onHover = useCallback(async () => {
		if (EditorPreloadManager.shouldPreload(contentId)) {
			setDoPreload(true);
			setEditSource(editSource);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, editSource]);

	return (
		<Fragment>
			{expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true) ? (
				<HoverAreaWrapper
					ref={
						getHasMatchMedia() &&
						predictivelyPrefetch &&
						expValEquals('platform_editor_predictive_collabdraft_prefetch', 'isEnabled', true)
							? elementRef
							: null
					}
					onMouseEnter={
						getHasMatchMedia() &&
						predictivelyPrefetch &&
						expValEquals('platform_editor_predictive_collabdraft_prefetch', 'isEnabled', true)
							? undefined
							: onHover
					}
				>
					{children}
				</HoverAreaWrapper>
			) : (
				<div onMouseEnter={onHover} onFocus={onHover}>
					{children}
				</div>
			)}
			{editSource === contextEditSource && doPreload && preloader
				? preloader({
						contentId,
						spaceKey,
						isEmbeddedEditor,
					})
				: null}
		</Fragment>
	);
};
